<template>
  <div>
    <popup2 :value="!!event" @input="event = null" width="428">
      <edit-field
        v-if="event === 'addGroup'"
        :type="'chips'"
        :title="'Add datasets to groups'"
        :field="'Groups'"
        :value="[]"
      />
      <default-confirmation
        text-delete="Delete this datasets completely?"
        v-else-if="event === 'delete'"
        @close="event = null"
        @delete="deleteAction"
      />
      <component
        v-else-if="event"
        :entity="{}"
        :is="event"
        :loading="loading"
        entity-type="DATASET"
        type="DATASET"
        :callback-after="load"
        @save="saveHandler"
        @close="event = null"
      />
    </popup2>
    <table-page
      :toolbar="toolbar"
      :table="table"
      :entity="true"
      :selected-out="eValue"
      @event="eventHandler"
    />
  </div>
</template>

<script>
import pageActions from './pageActions';
import selectedActions from './selectedActions';
import {
  ref,
  computed,
  onMounted,
  watch,
  reactive,
  toRefs,
  onUnmounted
} from '@vue/composition-api';
import DefaultConfirmation from '@/components/app-entity/modals/DefaultConfirmation';
import usedatasets from '@/api/datasets';
import useSettings from '@/api/tableSettings';
import toSnakeCase from 'lodash.snakecase';
import { nanoid } from 'nanoid';
import useSchemas from '@/api/schemas';
import useGroups from '@/api/groups';
import useMedia from '@/compositions/useMedia';
import useAuth from '@/api/auth';
import useCommonModal from '@/compositions/useCommonModal';
import { RELOAD_INTERVAL_SEC } from "@/utils/constants";
import cloneDeep from "lodash.clonedeep";
export default {
  name: 'datasets',
  components: {
    DefaultConfirmation,
    addNew: () => import('@/components/app-entity/modals/CreateObject'),
    changeSchema: () => import('@/components/app-entity/modals/EditScheme')
  },
  metaInfo: {
    titleTemplate: '%s | Datasets'
  },
  setup(props, { root }) {
    const { load: loadSettings, save: saveSettings } = useSettings();
    const { datasets: datasetsApi } = new usedatasets();
    const { schemas: schemasApi } = new useSchemas();
    const { groups: pureGroupsApi } = new useGroups();
    const { computeIcon } = useMedia();
    const modalComposition = useCommonModal();

    const { profile } = useAuth();
    const {
      total,
      visibleFields,
      list,
      load: loadList,
      params,
      loading,
      setParams
    } = datasetsApi;

    const visibleColumns = ref([]);
    const allColumns = ref([
      'name',
      'favourite',
      'muted',
      'enabled',
      'drivername',
      'editorgroup',
      'usergroup',
      'readergroup'
    ]);

    const showOnMobile = ['favourite', 'enabled'];

    const eValue = ref(null);
    let timerId = null;
    watch(
      () => root.$route.query.filterTable,
      () => {
        load();
      }
    );

    function deleteAction() {
      datasetsApi.remove(eValue.value.map(i => ({ id: i.id }))).then(() => {
        load();
        event.value = null;
        eValue.value = [];
      });
    }
    function load(filterOuter = {}) {
      let filter = {
        ...params.value.filter,
        ...filterOuter,
        schemaType: { equalTo: 'dataset' }
      };

      if (root.$route.query.filterTable !== undefined) {
        filter.name = {
          includesInsensitive: root.$route.query.filterTable
        };
      }

      setParams({ filter });

      return loadList().finally(() => {
        const setts = loadSettings('datasetsSettings');
        if (setts) {
          visibleFields.value = setts;
        }
      });
    }

    const event = ref(null);
    const bufferSelected = ref([]);
    const toolbar = ref({
      title: 'datasets',
      showAccess: true,
      pageActions,
      selectedActions
    });
    const settings = computed(() => {
      return visibleColumns.value;
    });
    const columns = computed(() => {
      return allColumns.value.map(item => ({
        key: item,
        text: root.$t(`object.${item}`),
        value: item,
        showOnMobile: showOnMobile.includes(item)
      }));
    });
    const items = computed(() =>
      list.value.map(i => ({
        ...i,
        drivername: i.schemaname,
        iconFallback: 'mdi-puzzle',
        avatar: computeIcon(i.schema)
      }))
    );
    const count = computed(() => total.value);
    const isLoading = computed(() => loading.value);
    const state = reactive({
      schemasList: [],
      usersGroup: [],
      table: {
        filters: [],
        total: count,
        items,
        columns,
        settings,
        loading: isLoading,
        itemRoute: 'datasetsTableComponent'
      }
    });
    function eventHandler({ event: e, data }) {
      eValue.value = data;
      switch (e) {
        case 'setSettings': {
          visibleColumns.value = data;
          saveSettings('datasetsSettings', data);
          break;
        }
        case 'delete': {
          event.value = 'delete';
          break;
        }
        case 'addNew': {
          event.value = 'addNew';
          break;
        }
        case 'addGroup': {
          event.value = 'addGroup';
          break;
        }
        case 'changeSchema': {
          bufferSelected.value = data;
          event.value = 'changeSchema';
          break;
        }
        case 'massiveAccessRights': {
          modalComposition.modal.show({
            component: () =>
              import('@/components/app-entity/modals/AppDialog.vue'),
            bind: {
              nameModal: 'EditRightsGroup'
            },
            slots: {
              default: {
                component: () =>
                  import('@/components/app-entity/modals/EditRightsGroup'),
                bind: {
                  entity: null
                },
                on: {
                  close() {
                    modalComposition.modal.hide('EditRightsGroup');
                  },
                  save(e) {
                    const payload = {};

                    if (e.editorgroup) {
                      payload.editorgroup = e.editorgroup;
                    }
                    if (e.readergroup) {
                      payload.readergroup = e.readergroup;
                    }
                    if (e.usergroup) {
                      payload.usergroup = e.usergroup;
                    }

                    datasetsApi
                      .update(
                        eValue.value.map(i => ({
                          id: i.id,
                          ...payload
                        }))
                      )
                      .then(() => {
                        load();
                        eValue.value = [];
                        event.value = null;
                      });
                    modalComposition.modal.hide('EditRightsGroup');
                  }
                }
              }
            }
          });
          break;
        }
        case 'massiveDisable': {
          datasetsApi
            .update(eValue.value.map(i => ({ id: i.id, enabled: false })))
            .then(() => {
              load();
              event.value = null;
              eValue.value = [];
            });
          break;
        }
        case 'massiveEnable': {
          datasetsApi
            .update(eValue.value.map(i => ({ id: i.id, enabled: true })))
            .then(() => {
              load();
              event.value = null;
              eValue.value = [];
            });
          break;
        }
        case 'setFilters': {
          state.table.filters = cloneDeep(data);

          let filter = {
            schemaType: { equalTo: 'dataset' }
          };
          data
            .filter(i => {
              if (Array.isArray(i.model) && i.model.length > 0) {
                return true;
              } else if (!Array.isArray(i.model) && i.model.value !== null) {
                return true;
              }
              return false;
            })
            .forEach(i => {
              filter = {
                ...filter,
                ...i?.getFilter()
              };
            });
          setParams({ filter, offset: 0 });
          load(filter);
          break;
        }
        case 'filteringTable': {
          const result = {};

          if (data.itemsPerPage) {
            result.first = data.itemsPerPage;
          }

          result.offset = (data.page - 1) * data.itemsPerPage;

          if (data.desc) {
            setParams({ desc: data.desc.length ? data.desc[0] : false });
          }

          if (data.sort) {
            if (data.sort.length) {
              setParams({ sort: data.sort[0] });
            } else {
              setParams({ sort: null });
              setParams({ orderBy: 'NATURAL' });
            }
          }

          if (data.desc && params.value.sort) {
            if (params.value.sort === 'drivername') {
              result.orderBy = 'SCHEMA_NAME';
            } else {
              result.orderBy = toSnakeCase(params.value.sort).toUpperCase();
            }
            result.orderBy += `_${params.value.desc ? 'DESC' : 'ASC'}`;
          }

          setParams(result);
          load();
          break;
        }
      }
    }
    function saveHandler(data) {
      switch (event.value) {
        case 'addNew': {
          datasetsApi
            .save({
              object: {
                schemaId: data.schema,
                name: data.name,
                enabled: data.enabled,
                description: data.description,
                editorgroup: data.editorgroup
              }
            })
            .then(r => {
              event.value = null;
              eValue.value = [];
              root.$router.push({
                name: 'datasetsTableComponent',
                params: { id: r.data.createObject.object.id }
              });
              load();
            });
          break;
        }
        case 'changeSchema': {
          datasetsApi
            .update(
              bufferSelected.value.map(item => ({
                id: item.id,
                schemaId: data
              }))
            )
            .then(r => {
              event.value = null;
              eValue.value = [];
              load();
            });
          break;
        }
      }
    }
    onUnmounted(() => {
      clearInterval(timerId);
      setParams(null);
    });

    onMounted(async () => {
      setParams({
        first:
          profile.value?.objectProperties.find(
            item => item.key === 'programNumbersOfRows'
          )?.value || 20,
        filter: {
          schemaType: { equalTo: 'dataset' },
          hardLinked: { equalTo: false }
        }
      });

      timerId = setInterval(load, RELOAD_INTERVAL_SEC);

      const setts = loadSettings('datasetsSettings');

      if (setts) {
        visibleColumns.value = setts;
      } else {
        visibleColumns.value = allColumns.value;
      }

      await load();
      await schemasApi
        .loadPureSchemata('DATASET', {
          parentSchemaExists: false
        })
        .then(data => {
          state.schemasList = data.schemata.map(schema => ({
            id: schema.id,
            value: schema.id,
            name: schema.name
          }));
        });
      await pureGroupsApi.load().then(data => {
        state.usersGroup = data.userGroups.map(group => ({
          id: group.id,
          value: group.id,
          name: group.groupName
        }));
      });
      await root.$nextTick();
      const filters = [
        {
          id: 'favourite',
          title: 'Favorite',
          getFilter() {
            return {
              favourite: {
                in: this.model.map(item => item.value)
              }
            };
          },
          model: [],
          type: 'tags',
          values: [
            {
              value: true,
              name: 'Yes',
              id: nanoid()
            },
            {
              name: 'No',
              value: false,
              id: nanoid()
            }
          ]
        },
        {
          title: 'Muted',
          id: 'muted',
          type: 'tags',
          getFilter() {
            return {
              muted: {
                in: this.model.map(item => item.value)
              }
            };
          },
          model: [],
          values: [
            { name: 'Yes', id: nanoid(), value: true },
            { name: 'No', id: nanoid(), value: false }
          ]
        },
        {
          id: 'enabled',
          title: 'Status',
          type: 'tags',
          getFilter() {
            return {
              enabled: {
                in: this.model.map(item => item.value)
              }
            };
          },
          model: [],
          values: [
            { name: 'Enabled', id: nanoid(), value: true },
            { name: 'Disabled', id: nanoid(), value: false }
          ]
        },
        {
          getFilter() {
            return {
              schemaName: {
                in: this.model.map(item => item.name)
              }
            };
          },
          id: 'Schemas name',
          model: [],
          type: 'tags',
          values: state.schemasList,
          title: 'Schema name'
        },
        {
          getFilter() {
            return {
              editorgroup: {
                in: this.model.map(item => item.id)
              }
            };
          },
          id: 'Editors group',
          model: [],
          type: 'tags',
          values: state.usersGroup,
          title: 'Editors group'
        },
        {
          getFilter() {
            return {
              usergroup: {
                in: this.model.map(item => item.id)
              }
            };
          },
          id: 'Users group',
          model: [],
          type: 'tags',
          values: state.usersGroup,
          title: 'Users group'
        },
        {
          getFilter() {
            return {
              readergroup: {
                in: this.model.map(item => item.id)
              }
            };
          },
          id: 'Readers group',
          model: [],
          type: 'tags',
          values: state.usersGroup,
          title: 'Readers group'
        }
      ];
      state.table.filters = cloneDeep(filters);
      state.table.emptyFilters = cloneDeep(filters);
    });
    return {
      ...toRefs(state),
      event,
      toolbar,
      eventHandler,
      saveHandler,
      deleteAction,
      eValue,
      load,
      loading: datasetsApi.loading
    };
  }
};
</script>
